/**
 * ApiTab Component
 *
 * Displays enabled/disabled API endpoints grouped by category.
 */

import React, { useMemo } from 'react'
import { Card, Tag, Space, Typography, Empty, Row, Col, Tooltip } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ApiOutlined,
  ReadOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  SearchOutlined,
  OrderedListOutlined,
  QuestionCircleOutlined,
  NumberOutlined,
  DownloadOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import type { TabProps, EndpointGroup } from '../types'

const { Text } = Typography

/**
 * Get icon for specific endpoint
 */
function getEndpointIcon(endpoint: string): React.ReactNode {
  const iconMap: Record<string, React.ReactNode> = {
    list: <UnorderedListOutlined />,
    get: <ReadOutlined />,
    count: <NumberOutlined />,
    exists: <QuestionCircleOutlined />,
    ids: <OrderedListOutlined />,
    distinct: <CopyOutlined />,
    search: <SearchOutlined />,
    create: <EditOutlined />,
    update: <EditOutlined />,
    delete: <DeleteOutlined />,
    patch: <EditOutlined />,
    export: <DownloadOutlined />,
    metadata: <ApiOutlined />
  }

  // Check for partial matches
  for (const [key, icon] of Object.entries(iconMap)) {
    if (endpoint.toLowerCase().includes(key)) {
      return icon
    }
  }

  return <ApiOutlined />
}

/**
 * Endpoint badge component
 */
function EndpointBadge({
  endpoint,
  enabled
}: {
  endpoint: string
  enabled: boolean
}): React.ReactElement {
  return (
    <Tooltip title={enabled ? 'Endpoint enabled' : 'Endpoint disabled'}>
      <Tag
        icon={enabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        color={enabled ? 'success' : 'default'}
        style={{
          margin: '4px',
          opacity: enabled ? 1 : 0.6
        }}
      >
        <Space size={4}>
          {getEndpointIcon(endpoint)}
          <span>{endpoint}</span>
        </Space>
      </Tag>
    </Tooltip>
  )
}

/**
 * Get category for a single endpoint using exact matching (no .includes() bugs)
 *
 * FIX: Check bulk patterns FIRST before general matches to prevent
 * "bulk_create" matching "create" in Write Operations.
 */
function getEndpointCategory(endpoint: string): string {
  const name = endpoint.toLowerCase()

  // BULK - check FIRST with explicit patterns
  if (name.startsWith('bulk_') ||
      name.endsWith('_many') ||
      name === 'upsert_many' ||
      name === 'create_many' ||
      name === 'update_many' ||
      name === 'delete_many') {
    return 'Bulk Operations'
  }

  // EXACT matches for standard operations
  const exactMap: Record<string, string> = {
    'list': 'Read Operations',
    'get': 'Read Operations',
    'count': 'Read Operations',
    'exists': 'Read Operations',
    'ids': 'Read Operations',
    'distinct': 'Read Operations',
    'search': 'Read Operations',
    'create': 'Write Operations',
    'update': 'Write Operations',
    'delete': 'Write Operations',
    'patch': 'Write Operations',
    'upsert': 'Write Operations',
    'metadata': 'Metadata',
    'metadata-read': 'Metadata',
    'schema': 'Metadata',
    'export': 'Export',
    'export_csv': 'Export',
    'export_json': 'Export',
    'download': 'Export',
    'activate': 'Lifecycle',
    'deactivate': 'Lifecycle',
    'archive': 'Lifecycle',
    'restore': 'Lifecycle',
    'expire': 'Lifecycle',
    'modify': 'Lifecycle',
    'transition': 'Lifecycle'
  }

  if (exactMap[name]) {
    return exactMap[name]
  }

  return 'Other'
}

/**
 * Categorize endpoints by grouping them using exact match categorization
 */
function categorizeEndpoints(endpoints: Record<string, boolean>): EndpointGroup[] {
  const categoryMap: Record<string, Array<{ name: string; enabled: boolean }>> = {}

  for (const [endpointName, enabled] of Object.entries(endpoints)) {
    const category = getEndpointCategory(endpointName)

    if (!categoryMap[category]) {
      categoryMap[category] = []
    }
    categoryMap[category].push({ name: endpointName, enabled })
  }

  // Convert to array and sort
  const categoryOrder = ['Read Operations', 'Write Operations', 'Bulk Operations', 'Lifecycle', 'Metadata', 'Export', 'Other']

  return categoryOrder
    .filter(cat => categoryMap[cat] && categoryMap[cat].length > 0)
    .map(cat => ({
      category: cat,
      endpoints: categoryMap[cat].sort((a, b) => a.name.localeCompare(b.name))
    }))
}

/**
 * Derive API path from schema name using standard naming conventions
 */
function deriveApiPath(schemaName?: string): string {
  if (!schemaName) return '(unknown)'

  // Convert camelCase/PascalCase to snake_case, then to API path
  // e.g., 'offering' -> '/offerings', 'auditLog' -> '/audit_logs'
  const snakeCase = schemaName
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '')

  // Pluralize for API path
  if (snakeCase.endsWith('y')) {
    return `/${snakeCase.slice(0, -1)}ies`
  }
  // If word ends in 's' but not 'ss', it's already plural - don't add anything
  // e.g., 'offerings' stays '/offerings', not '/offeringses'
  if (snakeCase.endsWith('s') && !snakeCase.endsWith('ss')) {
    return `/${snakeCase}`
  }
  // Words ending in 'ss' get 'es': 'class' -> '/classes'
  if (snakeCase.endsWith('ss')) {
    return `/${snakeCase}es`
  }
  return `/${snakeCase}s`
}

export function ApiTab({ metadata, schemaName }: TabProps): React.ReactElement {
  const api = metadata.api

  // Determine display path - use api.path if specified, otherwise derive from schema name
  const displayPath = api?.path || deriveApiPath(schemaName)
  const isPathDerived = !api?.path

  // Categorize endpoints
  const groupedEndpoints = useMemo(() => {
    if (!api?.endpoints) return []
    return categorizeEndpoints(api.endpoints)
  }, [api?.endpoints])

  // Calculate stats
  const stats = useMemo(() => {
    if (!api?.endpoints) return { total: 0, enabled: 0, disabled: 0 }
    const entries = Object.entries(api.endpoints)
    return {
      total: entries.length,
      enabled: entries.filter(([_, enabled]) => enabled).length,
      disabled: entries.filter(([_, enabled]) => !enabled).length
    }
  }, [api?.endpoints])

  if (!api || !api.endpoints || Object.keys(api.endpoints).length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No API endpoint metadata available"
      />
    )
  }

  return (
    <div>
      {/* Summary header */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Row gutter={16} align="middle">
          <Col flex="1">
            <Space direction="vertical" size={0}>
              <Text type="secondary">API Path</Text>
              <Space>
                <Text strong code>{displayPath}</Text>
                {isPathDerived && (
                  <Tooltip title="Path derived from schema name (not explicitly configured)">
                    <Tag color="default" style={{ fontSize: '10px' }}>derived</Tag>
                  </Tooltip>
                )}
              </Space>
            </Space>
          </Col>
          <Col>
            <Space size={16}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 600, color: '#52c41a' }}>
                  {stats.enabled}
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>Enabled</Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 600, color: '#d9d9d9' }}>
                  {stats.disabled}
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>Disabled</Text>
              </div>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Two-column layout */}
      <Row gutter={16}>
        {/* Enabled endpoints */}
        <Col span={12}>
          <Card
            size="small"
            title={
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <span>Enabled Endpoints</span>
              </Space>
            }
            style={{ height: '100%' }}
          >
            {groupedEndpoints.map((group) => {
              const enabledInGroup = group.endpoints.filter(e => e.enabled)
              if (enabledInGroup.length === 0) return null

              return (
                <div key={group.category} style={{ marginBottom: '12px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {group.category}
                  </Text>
                  <div style={{ marginTop: '4px' }}>
                    {enabledInGroup.map((endpoint) => (
                      <EndpointBadge
                        key={endpoint.name}
                        endpoint={endpoint.name}
                        enabled={true}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
            {stats.enabled === 0 && (
              <Text type="secondary">No enabled endpoints</Text>
            )}
          </Card>
        </Col>

        {/* Disabled endpoints */}
        <Col span={12}>
          <Card
            size="small"
            title={
              <Space>
                <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
                <span>Disabled Endpoints</span>
              </Space>
            }
            style={{ height: '100%' }}
          >
            {groupedEndpoints.map((group) => {
              const disabledInGroup = group.endpoints.filter(e => !e.enabled)
              if (disabledInGroup.length === 0) return null

              return (
                <div key={group.category} style={{ marginBottom: '12px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {group.category}
                  </Text>
                  <div style={{ marginTop: '4px' }}>
                    {disabledInGroup.map((endpoint) => (
                      <EndpointBadge
                        key={endpoint.name}
                        endpoint={endpoint.name}
                        enabled={false}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
            {stats.disabled === 0 && (
              <Text type="secondary">No disabled endpoints</Text>
            )}
          </Card>
        </Col>
      </Row>

    </div>
  )
}

export default ApiTab
